<template>
    <LoadingCard v-if="loading" />
    <div v-else>
        <div class="toolbar">
            <div class="toolbar__label">
                Orchard Maps
            </div>
            <div class="actions">
                <i class="actions__item zmdi zmdi-search" @click="enableSearch"></i>
                <i class="actions__item zmdi zmdi-plus" v-b-tooltip="'Add a map'" @click="enableCreatingMap"></i>
            </div>

            <div class="toolbar__search" :class="searchEnabled ? 'd-block' : 'd-none'">
                <input type="text" placeholder="Search..." ref="searchInput" v-model="searchTerm">
                <i class="toolbar__search__close zmdi zmdi-long-arrow-left" @click="disableSearch"></i>
            </div>
        </div>

        <div class="card">
            <div class="card-header bg-primary text-white">
                Orchard Maps
            </div>
            <b-table small hover stacked="md" :items="filteredData" :fields="table.fields" sort-by="name" :show-empty="true">
                <template slot="actions" slot-scope="row">

                    <b-button variant="secondary btn--icon"
                              v-b-tooltip.hover="'Edit Map'"
                              @click.prevent="enableEditingMap(row.item)">
                        <i class="zmdi zmdi-edit"></i>
                    </b-button>

                    <a :href="row.item.image" :download="buildFileName(row.item)" class="btn btn-orange btn--icon"
                       v-b-tooltip="'Download Map'"
                       v-if="row.item.image" target="_blank">
                        <i class="zmdi zmdi-download"></i>
                    </a>
                    <button class="btn btn-orange btn--icon" disabled v-else>
                        <i class="zmdi zmdi-download"></i>
                    </button>
                </template>
            </b-table>
        </div>

        <b-modal id="editModal" size="md" ref="modal" v-model="editingEnabled" :lazy="true"
                 :title="`${editingMap ? 'Edit' : 'New'} Orchard Map`"
                 :ok-disabled="buttonsDisabled" :cancel-disabled="buttonsDisabled"
                 @hidden="disableEditingMap" @ok="handleMapSave">

            <div class="form-group">
                <label>Name</label>
                <div class="position-relative">
                    <input type="text" name="name" data-vv-as="name" v-validate="{required: true}" class="form-control" v-model="name">
                    <div class="invalid-feedback" v-if="errors.has('name')">{{ errors.first('name') }}</div>
                    <i class="form-group__bar"></i>
                </div>
            </div>

            <div class="form-group">
                <label>Map Image
                    <template v-if="editingMap"> (leave empty if you do not wish to change)</template>
                </label>
                <div class="position-relative">
                    <b-form-file v-model="image" @input="uploadNewMapImage" accept="image/*" placeholder="Choose an image..." />
                    <b-progress :value="imageProgress" animated v-if="imageProgress"
                                :variant="imageProgressVariant" />
                </div>
            </div>

            <template slot="modal-footer">
                <button class="btn btn-link text-danger mr-auto" @click="handleDelete" v-if="editingMap">
                    Delete
                </button>
                <button class="btn btn-secondary" @click="disableEditingMap">
                    Cancel
                </button>
                <button class="btn btn-primary" :disabled="buttonsDisabled" @click="handleMapSave">
                    Save
                </button>
            </template>
        </b-modal>
    </div>
</template>
<script>
import createOrchardMapMutation from '@views/Setup/OrchardMapManager/mutationCreateOrchardMap.graphql';
import updateOrchardMapMutation from '@views/Setup/OrchardMapManager/mutationUpdateOrchardMap.graphql';
import deleteOrchardMapMutation from '@views/Setup/OrchardMapManager/mutationDeleteOrchardMap.graphql';
import listOrchardMapsQuery from '@views/Setup/OrchardMapManager/queryListOrchardMaps.graphql';

const datePickerConfig = (config = {}) => {
  return {
    wrap: true, // set wrap to true only when using 'input-group'
    altInput: true,
    enableTime: false,
    nextArrow: '<i class="zmdi zmdi-long-arrow-right" />',
    prevArrow: '<i class="zmdi zmdi-long-arrow-left" />',
    ...config,
  };
};

/**
 * @property {String} searchTerm
 */
export default {
  name: 'OrchardMapManager',
  apollo: {
    orchardMaps: {
      query: listOrchardMapsQuery,
      update: data => data.filterOrchardMaps.data,
    },
  },
  data() {
    return {
      orchardMaps: [],
      searchEnabled: false,
      editingEnabled: false,
      editingMap: null,
      name: null,
      image: null,
      imageProgress: 0,
      imageProgressVariant: 'primary',
      imageId: null,
      buttonsDisabled: false,
      expiry: null,
      searchTerm: '',
      loading: 0,
      datePicker: datePickerConfig({
        altFormat: 'M j, Y',
        dateFormat: 'Y-m-d',
      }),
    };
  },
  computed: {
    table() {
      return {
        fields: [
          { key: 'name', label: 'Name' },
          { key: 'actions', label: '', class: 'text-right' },
        ],
      };
    },
    filteredData() {
      const subjectMatchesSearch = subject => {
        return this.searchTerm.length === 0 || subject.name.toLowerCase().includes(this.searchTerm.toLowerCase());
      };

      return this.orchardMaps.filter(subjectMatchesSearch).sort((a, b) => a.name.localeCompare(b.name));
    },
  },
  mounted() {
  },
  methods: {
    buildFileName(item) {
      if (item.image) {
        const extension = item.image.substr(item.image.lastIndexOf('.'));
        return `${item.name}${extension}`;
      }
    },
    uploadNewMapImage(file) {
      this.buttonsDisabled = true;
      this.imageId = null;
      if (file) {
        this.$uploadSingleFile(file, {}, progress => {
            this.imageProgress = Math.round(progress.loaded * 100 / progress.total);
          })
          .then(({ data: { file_id } }) => {
            this.buttonsDisabled = false;
            this.imageProgressVariant = 'success';
            this.imageId = file_id;
            console.log(file_id);
          })
          .catch(err => {
            this.buttonsDisabled = false;
            this.imageProgressVariant = 'danger';
            console.error('failed', err);
            this.$notifyFailure('Unfortunately, we were unable to do that!');
          });
      }
    },
    handleMapSave(evt) {
      evt.preventDefault();
      this.$validator.validateAll().then(validationWasSuccessful => {
        if (validationWasSuccessful && this.editingMap) {
          this.updateMap();
        } else if (validationWasSuccessful) {
          this.createMap();
        }
      });
    },

    updateMap() {
      const input = {
        id: this.editingMap.id,
        name: this.name,
      };

      if (this.imageId) {
        input.image_id = this.imageId;
      }

      this.$apollo.mutate({
          mutation: updateOrchardMapMutation,
          variables: { input },
        })
        .then(() => {
          this.$notifySuccess('Orchard Map updated!');
          this.disableEditingMap();
        })
        .catch(err => {
          this.$mapGraphQLError(err);
        })
        .catch(err => {
          console.error(err);
          this.$notifyFailure('Unfortunately, we were unable to do that!');
        });
    },
    createMap() {
      const input = {
        name: this.name,
      };

      if (this.imageId) {
        input.image_id = this.imageId;
      }

      this.$apollo.mutate({
          mutation: createOrchardMapMutation,
          variables: { input },
        })
        .then(() => {
          this.$notifySuccess('Orchard Map created!');
          this.$apollo.queries.orchardMaps.refetch();
          this.disableEditingMap();
        })
        .catch(err => {
          this.$mapGraphQLError(err);
        })
        .catch(err => {
          console.error(err);
          this.$notifyFailure('Unfortunately, we were unable to do that!');
        });
    },
    enableCreatingMap() {
      this.editingEnabled = true;
    },
    enableEditingMap(map) {
      this.editingMap = map;
      this.name = map.name;
      this.editingEnabled = true;
    },
    disableEditingMap() {
      this.editingMap = null;
      this.name = null;
      this.image = null;
      this.imageId = null;
      this.imageProgress = 0;
      this.imageProgressVariant = 'primary';
      this.editingEnabled = false;
    },
    enableSearch() {
      this.searchEnabled = true;
      this.$nextTick(() => this.$refs.searchInput.focus());
    },
    disableSearch() {
      this.searchTerm = '';
      this.searchEnabled = false;
    },
    handleDelete() {
      this.$apollo
        .mutate({
          mutation: deleteOrchardMapMutation,
          variables: {
            input: {
              id: this.editingMap.id,
            },
          },
        })
        .then(() => {
          this.$notifySuccess('Orchard map deleted!');
          this.$apollo.queries.orchardMaps.refetch();
          this.disableEditingMap();
        })
        .catch(err => this.$mapGraphQLError(err))
        .catch(err => {
          console.error(err);
          this.$notifyFailure('Unfortunately, we were unable to do that!',);
        });
    },
  },
};
</script>
